import { useEffect, useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import ConfirmDialog from '../../common/ConfirmDialog';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deletePricingRate,
  getPricingItems,
  getPricingRates,
  IPricingRate,
} from '../../services/pricingService';

const PricingRates = () => {
  const navigate = useNavigate();
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const ratesRef = useRef<IPagedDataGridRefType>(null);
  const [selectedRate, setSelectedRate] = useState<IPricingRate>();
  const [itemNames, setItemNames] = useState<Record<number, string>>({});
  const [
    isDeleteRateOpen,
    { setTrue: showDeleteRate, setFalse: hideDeleteRate },
  ] = useBoolean(false);

  useEffect(() => {
    const abortController = new AbortController();
    getPricingItems(abortController, 500, 1)
      .then((page) => {
        const names: Record<number, string> = {};
        page.items.forEach((item) => {
          names[item.id] = item.name || item.code;
        });
        setItemNames(names);
      })
      .catch(() => undefined);
    return () => abortController.abort();
  }, []);

  const rateColumns: DataGridColumn<IPricingRate>[] = [
    {
      key: 'pricingItemId',
      name: 'Pricing item',
      minWidth: 140,
      width: 220,
      isSortable: true,
      value: (item) =>
        itemNames[item.pricingItemId] ?? String(item.pricingItemId),
    },
    {
      key: 'currencyCode',
      name: 'Currency',
      minWidth: 80,
      width: 110,
      isSortable: true,
    },
    {
      key: 'amount',
      name: 'Amount',
      minWidth: 80,
      width: 120,
      isSortable: true,
    },
    {
      key: 'effectiveFrom',
      name: 'Effective from',
      minWidth: 140,
      width: 180,
      isSortable: true,
      type: 'date',
    },
    {
      key: 'effectiveTo',
      name: 'Effective to',
      minWidth: 140,
      width: 180,
      type: 'date',
    },
  ];

  const rateCommands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'newRate',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newPricingRate');
        },
        disabled: !isAdmin,
      },
      {
        key: 'editRate',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selectedRate)
            void navigate(`/editPricingRate?id=${selectedRate.id}`);
        },
        disabled: !isAdmin || !selectedRate,
      },
      {
        key: 'deleteRate',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: () => showDeleteRate(),
        disabled: !isAdmin || !selectedRate,
      },
    ],
    [isAdmin, navigate, selectedRate, showDeleteRate]
  );

  return (
    <>
      <PagedDataGrid<IPricingRate>
        ref={ratesRef}
        columns={rateColumns}
        getAction={getPricingRates}
        commandBarItems={rateCommands}
        selectionMode="single"
        searchable={false}
        onSelectionChange={setSelectedRate}
      />
      <ConfirmDialog
        isModalOpen={isDeleteRateOpen}
        hideModal={hideDeleteRate}
        message={`Pricing rate ${
          selectedRate?.id ?? ''
        } would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteRate();
          if (!selectedRate) return;
          const abortController = new AbortController();
          deletePricingRate(abortController, selectedRate.id)
            .then(() => {
              ratesRef.current?.refresh();
              setSuccessMessage(`Pricing rate ${selectedRate.id} deleted.`);
              setSelectedRate(undefined);
            })
            .catch((error: { message?: string }) => {
              setErrorMessage(
                error.message ?? 'Failed to delete pricing rate.'
              );
            });
        }}
      />
    </>
  );
};

export default PricingRates;
